import { useState } from 'react'
import { motion } from 'framer-motion'
import { 
  Printer, 
  Thermometer, 
  Clock, 
  Layers,
  Play,
  Pause,
  RefreshCw,
  Wifi,
  WifiOff,
  Camera,
  ChevronRight,
  AlertTriangle,
  CheckCircle,
  Zap,
  Plus,
  Square,
} from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { usePrinterConnectionStore, useAllPrinterStatuses } from '@/stores/printerConnectionStore'
import type { PrinterState, ConnectionStatus } from '@/lib/printerConnection.types'
import AddPrinterModal from '@/components/printer/AddPrinterModal'
import { cn, formatDuration } from '@/lib/utils'

const statusColors: Record<string, string> = {
  idle: 'bg-surface-500',
  printing: 'bg-success-500',
  paused: 'bg-warning-500',
  error: 'bg-error-500',
  offline: 'bg-surface-600',
  heating: 'bg-warning-500',
}

const statusLabels: Record<string, string> = {
  idle: 'Idle',
  printing: 'Printing',
  paused: 'Paused',
  error: 'Error',
  offline: 'Offline',
  heating: 'Heating',
}

function PrinterCard({ printerState }: { printerState: PrinterState }) {
  const { pausePrint, resumePrint, cancelPrint } = usePrinterConnectionStore()
  
  const isConnected = printerState.connectionStatus === 'connected'
  const status = printerState.printerStatus
  const telemetry = printerState.telemetry
  const config = printerState.config
  const currentFile = printerState.currentFile
  const isPrinting = status === 'printing'
  const isPaused = status === 'paused'

  return (
    <Card className="overflow-hidden">
      <div className="p-4 border-b border-surface-800 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className={cn(
            'w-10 h-10 rounded-xl flex items-center justify-center',
            isConnected ? 'bg-primary-500/20' : 'bg-surface-800'
          )}>
            <Printer className={cn(
              'w-5 h-5',
              isConnected ? 'text-primary-400' : 'text-surface-500'
            )} />
          </div>
          <div>
            <h3 className="font-semibold text-white">{config.name}</h3>
            <p className="text-xs text-surface-400 capitalize">{config.protocol}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className={cn(
            'px-2 py-1 rounded-full text-xs font-medium flex items-center gap-1.5',
            status === 'printing' && 'bg-success-500/20 text-success-400',
            status === 'idle' && 'bg-surface-700 text-surface-300',
            status === 'paused' && 'bg-warning-500/20 text-warning-400',
            status === 'error' && 'bg-error-500/20 text-error-400',
            status === 'offline' && 'bg-surface-800 text-surface-500',
            status === 'heating' && 'bg-warning-500/20 text-warning-400'
          )}>
            <span className={cn('w-1.5 h-1.5 rounded-full', statusColors[status] || 'bg-surface-500')} />
            {statusLabels[status] || status}
          </span>
          {isConnected ? (
            <Wifi className="w-4 h-4 text-success-400" />
          ) : (
            <WifiOff className="w-4 h-4 text-surface-500" />
          )}
        </div>
      </div>

      <CardContent className="p-4">
        {(isPrinting || isPaused) && telemetry ? (
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <div className="w-16 h-16 bg-surface-800 rounded-lg flex items-center justify-center flex-shrink-0">
                <Layers className="w-8 h-8 text-surface-600" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-medium text-white truncate">{currentFile || 'Unknown file'}</p>
                <p className="text-sm text-surface-400">
                  Layer {telemetry.layerInfo?.current || '-'} / {telemetry.layerInfo?.total || '-'}
                </p>
              </div>
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="text-surface-400">Progress</span>
                <span className="text-white font-medium">{Math.round(telemetry.printProgress || 0)}%</span>
              </div>
              <div className="h-2 bg-surface-800 rounded-full overflow-hidden">
                <motion.div
                  className="h-full bg-gradient-to-r from-primary-500 to-ai-500"
                  initial={{ width: 0 }}
                  animate={{ width: `${telemetry.printProgress || 0}%` }}
                />
              </div>
            </div>

            <div className="grid grid-cols-3 gap-3 text-sm">
              <div className="bg-surface-800 rounded-lg p-2 text-center">
                <p className="text-surface-500 text-xs">Layer</p>
                <p className="text-white font-medium">
                  {telemetry.layerInfo?.current || '-'}/{telemetry.layerInfo?.total || '-'}
                </p>
              </div>
              <div className="bg-surface-800 rounded-lg p-2 text-center">
                <p className="text-surface-500 text-xs">Remaining</p>
                <p className="text-white font-medium">
                  {telemetry.printTimeRemaining ? formatDuration(telemetry.printTimeRemaining) : '--'}
                </p>
              </div>
              <div className="bg-surface-800 rounded-lg p-2 text-center">
                <p className="text-surface-500 text-xs">Elapsed</p>
                <p className="text-white font-medium">
                  {telemetry.printTimeElapsed ? formatDuration(telemetry.printTimeElapsed) : '--'}
                </p>
              </div>
            </div>

            <div className="flex gap-2">
              {isPrinting && (
                <Button 
                  variant="secondary" 
                  size="sm" 
                  className="flex-1"
                  onClick={() => pausePrint(config.id)}
                >
                  <Pause className="w-4 h-4" /> Pause
                </Button>
              )}
              {isPaused && (
                <Button 
                  variant="primary" 
                  size="sm" 
                  className="flex-1"
                  onClick={() => resumePrint(config.id)}
                >
                  <Play className="w-4 h-4" /> Resume
                </Button>
              )}
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => {
                  if (confirm('Cancel this print?')) {
                    cancelPrint(config.id)
                  }
                }}
              >
                <Square className="w-4 h-4" />
              </Button>
              <Button variant="ghost" size="sm">
                <Camera className="w-4 h-4" />
              </Button>
            </div>
          </div>
        ) : (
          <div className="text-center py-6">
            <div className="w-12 h-12 bg-surface-800 rounded-xl mx-auto flex items-center justify-center mb-3">
              <Printer className="w-6 h-6 text-surface-500" />
            </div>
            <p className="text-surface-400 text-sm mb-4">No active print</p>
            <Button variant="primary" size="sm" disabled={!isConnected}>
              <Play className="w-4 h-4" /> Start Print
            </Button>
          </div>
        )}
      </CardContent>

      {isConnected && telemetry && (
        <div className="px-4 py-3 bg-surface-800/50 border-t border-surface-800 flex items-center gap-4">
          <div className="flex items-center gap-2 text-sm">
            <Thermometer className="w-4 h-4 text-warning-400" />
            <span className="text-surface-400">Nozzle</span>
            <span className="text-white font-medium">{Math.round(telemetry.temperatures.nozzle.actual)}°C</span>
          </div>
          <div className="flex items-center gap-2 text-sm">
            <Thermometer className="w-4 h-4 text-primary-400" />
            <span className="text-surface-400">Bed</span>
            <span className="text-white font-medium">{Math.round(telemetry.temperatures.bed.actual)}°C</span>
          </div>
          {telemetry.temperatures.chamber && (
            <div className="flex items-center gap-2 text-sm">
              <Thermometer className="w-4 h-4 text-ai-400" />
              <span className="text-surface-400">Chamber</span>
              <span className="text-white font-medium">{Math.round(telemetry.temperatures.chamber.actual)}°C</span>
            </div>
          )}
        </div>
      )}
    </Card>
  )
}

export default function PrintDashboard() {
  const [isAddModalOpen, setIsAddModalOpen] = useState(false)
  const [selectedTab, setSelectedTab] = useState<'printers' | 'queue' | 'history'>('printers')
  
  const printerStatuses = useAllPrinterStatuses()
  const savedPrinters = usePrinterConnectionStore((s) => s.savedPrinters)
  const printerStates = usePrinterConnectionStore((s) => s.printerStates)

  const activePrints = printerStatuses.filter(p => p.state?.printerStatus === 'printing').length
  const connectedPrinters = printerStatuses.filter(p => p.state?.connectionStatus === 'connected').length
  const totalPrinters = savedPrinters.length

  return (
    <div className="h-full overflow-y-auto p-6 space-y-6">
      {/* Header Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4 flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-primary-500/20 flex items-center justify-center">
              <Printer className="w-6 h-6 text-primary-400" />
            </div>
            <div>
              <p className="text-sm text-surface-400">Connected</p>
              <p className="text-2xl font-bold text-white">{connectedPrinters}/{totalPrinters}</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-success-500/20 flex items-center justify-center">
              <Play className="w-6 h-6 text-success-400" />
            </div>
            <div>
              <p className="text-sm text-surface-400">Active Prints</p>
              <p className="text-2xl font-bold text-white">{activePrints}</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-ai-500/20 flex items-center justify-center">
              <Zap className="w-6 h-6 text-ai-400" />
            </div>
            <div>
              <p className="text-sm text-surface-400">AI Success Rate</p>
              <p className="text-2xl font-bold text-white">94%</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-warning-500/20 flex items-center justify-center">
              <Clock className="w-6 h-6 text-warning-400" />
            </div>
            <div>
              <p className="text-sm text-surface-400">Queue</p>
              <p className="text-2xl font-bold text-white">3 jobs</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-2 border-b border-surface-800 pb-4">
        {(['printers', 'queue', 'history'] as const).map((tab) => (
          <button
            key={tab}
            onClick={() => setSelectedTab(tab)}
            className={cn(
              'px-4 py-2 rounded-lg text-sm font-medium transition-colors capitalize',
              selectedTab === tab
                ? 'bg-primary-600 text-white'
                : 'text-surface-400 hover:text-white hover:bg-surface-800'
            )}
          >
            {tab}
          </button>
        ))}
      </div>

      {/* Printers Grid */}
      {selectedTab === 'printers' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
          {savedPrinters.map((config) => {
            const state = printerStates.get(config.id)
            const printerState: PrinterState = state || {
              id: config.id,
              config,
              connectionStatus: 'disconnected' as ConnectionStatus,
              printerStatus: 'offline',
            }
            return <PrinterCard key={config.id} printerState={printerState} />
          })}
          
          {/* Add Printer Card */}
          <Card className="border-dashed border-2 border-surface-700 bg-transparent">
            <CardContent className="p-8 flex flex-col items-center justify-center text-center h-full min-h-[300px]">
              <div className="w-16 h-16 rounded-full bg-surface-800 flex items-center justify-center mb-4">
                <Printer className="w-8 h-8 text-surface-500" />
              </div>
              <h3 className="font-semibold text-white mb-2">Add Printer</h3>
              <p className="text-sm text-surface-400 mb-4">Connect a new 3D printer to your workspace</p>
              <Button variant="secondary" onClick={() => setIsAddModalOpen(true)}>
                <Plus className="w-4 h-4" /> Add Printer
              </Button>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Queue Tab */}
      {selectedTab === 'queue' && (
        <Card>
          <CardHeader>
            <CardTitle>Print Queue</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {[1, 2, 3].map((i) => (
                <div key={i} className="flex items-center gap-4 p-4 bg-surface-800 rounded-xl">
                  <div className="w-12 h-12 bg-surface-700 rounded-lg flex items-center justify-center">
                    <Layers className="w-6 h-6 text-surface-500" />
                  </div>
                  <div className="flex-1">
                    <p className="font-medium text-white">Queue Item {i}</p>
                    <p className="text-sm text-surface-400">PLA • 0.2mm • ~2h 30m</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button variant="ghost" size="icon-sm">
                      <Play className="w-4 h-4" />
                    </Button>
                    <Button variant="ghost" size="icon-sm">
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* History Tab */}
      {selectedTab === 'history' && (
        <Card>
          <CardHeader>
            <CardTitle>Print History</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {[
                { name: 'Phone Stand v2', status: 'success', time: '2h 15m', date: 'Today' },
                { name: 'Cable Organizer', status: 'success', time: '1h 45m', date: 'Yesterday' },
                { name: 'Test Print', status: 'failed', time: '0h 23m', date: '2 days ago' },
              ].map((item, i) => (
                <div key={i} className="flex items-center gap-4 p-4 bg-surface-800 rounded-xl">
                  <div className={cn(
                    'w-10 h-10 rounded-lg flex items-center justify-center',
                    item.status === 'success' ? 'bg-success-500/20' : 'bg-error-500/20'
                  )}>
                    {item.status === 'success' ? (
                      <CheckCircle className="w-5 h-5 text-success-400" />
                    ) : (
                      <AlertTriangle className="w-5 h-5 text-error-400" />
                    )}
                  </div>
                  <div className="flex-1">
                    <p className="font-medium text-white">{item.name}</p>
                    <p className="text-sm text-surface-400">{item.date} • {item.time}</p>
                  </div>
                  <Button variant="ghost" size="sm">
                    <RefreshCw className="w-4 h-4" /> Reprint
                  </Button>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Add Printer Modal */}
      <AddPrinterModal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
      />
    </div>
  )
}
